/**
 * career-search-profile — Workshop 2, »Was ich suche« (route `/workshops/search-profile`).
 *
 * DERIVE-FIRST redesign (W4). The retired W2 surface made the person AUTHOR raw role chips
 * and showed literal query strings, so it read as »my inputs are taken literally«. It now
 * leads with a DERIVATION: an AI meta-prompt reads the person's values-workshop output, their
 * personal statements, their CV/reference evidence and their own free-text intent, and PROPOSES
 * the target roles that best carry their unique value propositions — each role WITH a
 * one-sentence »warum das zu dir passt« rationale. The person reviews the proposal, refines it
 * with feedback (RLHF — the tool re-derives), and accepts it into their `SearchConfig`. The
 * tool proposes; the person disposes.
 *
 * Direct field editing is NOT gone — it is the fallback, disclosed as a secondary
 * »Feineinstellungen anpassen« section (§0 adaptive disclosure, closed by default) — so the
 * person feels the tool derived their profile rather than that they had to fill a form.
 *
 * Honest transparency stays: the »Suchanfragen« preview still shows the ACTUAL query strings
 * `buildQueries` will fire (the same function discovery rides — the preview cannot drift from
 * the real search), reframed as TOOL-DERIVED and secondary, not as literal inputs.
 *
 * §0 seamless document: hairline-separated sections, no `.surface-panel`. Containment is
 * reserved for the genuine affordances — the derive proposal's active review, the inputs, the
 * removable chips. The RLHF Feedback composer is the shared `refine-composer` helper.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"
import { DEFAULT_SEARCH_CONFIG, type MatchTier, type SearchConfig } from "@career/definition-v2"
import { I18nToken, RouterToken, SessionToken, apiGet, apiPost } from "../services"
import type { ShellSession } from "../domain"
import {
  createSearchProfileReviewDraftStore,
  type DerivedProfile,
  type SearchProfileReviewDraftStore,
} from "../lib/search-profile-draft"
import type { CareerI18n, CareerKey } from "../../i18n"
import { hubHero } from "../lib/hub"
import { nextStep } from "../lib/next-step"
import { spineDecl, toNextStep } from "../golden-path"
import { refineComposer, readRefineFeedback, type RefineComposerActs } from "../lib/refine-composer"

/**
 * The transport envelope of `GET/POST /api/workshops/search-profile`.
 *
 * `config` is the DOMAIN `SearchConfig`, imported — never a hand-rolled field list. The
 * wrapper around it is a transport shape, not a domain type, and follows the same local
 * convention every other surface here uses for its response envelope.
 */
interface SearchProfileView {
  config: SearchConfig
  /** Is W1 complete? False ⇒ guide the person into W1 rather than offer a derive whose
   *  accept the server-side prereq (`saveSearchProfile` → `startWorkshop`) would reject. */
  w1Ready: boolean
  targetingReady: boolean
  canStart: boolean
  reason?: "empty-targeting"
  queries: string[]
  proposal: { roles: string[]; seniority: string[]; locations: string[] }
  authoredSummary: string
  derivedSummary: string
}

/**
 * The transport shape of `POST /api/workshops/search-profile/derive` (the AI derive seam)
 * lives in `../lib/search-profile-draft` together with its structural guard — the proposal is
 * mirrored into localStorage so a page death cannot destroy a deliverable the person paid for,
 * and a shape whose guard sits elsewhere invites a cast at that untrusted boundary.
 */
interface DeriveResult {
  proposal: DerivedProfile
  runRef?: string
}

/** The read-only slice of `GET /api/workshops/decision-profile` this surface renders (S567 /
 *  P-A). Same endpoint `career-decision-profile` uses — no contract change, no second truth. */
interface RubricView {
  weights: { dimension: string; label: string; weight: number }[]
  antiValues: string[]
  authored: boolean
}

/** Where the rubric is EDITED. Rendered here, owned there — one writer (W3). */
const DECISION_PROFILE_ROUTE = "/workshops/profiler"

/** The seniority tokens the search understands — the ids ARE the search terms, so the
 *  German label a person reads and the token discovery matches on are the same string. */
const SENIORITY_CHOICES = ["Senior", "Lead", "Principal", "Bereichsleitung", "Geschäftsführung"] as const

/** The common hard exclusions, offered as one-tap toggles. These are the option ids the
 *  retired `exclusions` step used: the CONTENT was fine, the nine-step ceremony around it
 *  was not. They now feed a filter that genuinely drops postings (`isExcludedPosting`). */
const EXCLUSION_CHOICES = ["Praktikum", "Werkstudent", "Zeitarbeit", "Kaltakquise", "Schichtdienst"] as const

/** The location MODE German postings label themselves with — not a place. */
const REMOTE = "Remote"

/**
 * The quality floor, worst → best. `none` = no floor at all (persisted as `null`).
 *
 * `unsuited` is deliberately NOT offered: as a FLOOR it means "everything at or above the
 * bottom tier", which is just `none` under another name.
 */
const TIER_CHOICES = ["none", "weak", "viable", "strong"] as const
type TierChoice = (typeof TIER_CHOICES)[number]

/**
 * The label key per choice, as an EXPLICIT mapped constant rather than a
 * `t(`sp.tier.${tier}`)` template literal.
 *
 * A template-literal key is only checked when the whole union is resolved, so a key that is
 * never registered — or a mistyped prefix — compiles until something forces the check, and no
 * test ever catches it because the missing key only shows up as literal text on screen. A
 * `Record` over the choice tuple makes it exhaustive: add a choice and this fails to compile.
 */
const TIER_LABEL: Record<TierChoice, CareerKey> = {
  none: "sp.tier.none",
  weak: "sp.tier.weak",
  viable: "sp.tier.viable",
  strong: "sp.tier.strong",
}

/** Result budgets offered as discrete choices. A number field would be a change-event
 *  affordance, and `data-act` dispatch is click-only (web-kit `installActionDelegate`) —
 *  discrete buttons are both the working idiom and the clearer question to ask. */
const LIMIT_CHOICES: readonly number[] = [20, 40, 60, 100]

/** The RLHF composer's action verbs — the review's »passt nicht ganz?« Feedback loop. */
const REFINE_ACTS: RefineComposerActs = {
  open: "sp-refine-open",
  submit: "sp-refine-submit",
  cancel: "sp-refine-cancel",
  accept: "sp-refine-accept",
  again: "sp-refine-again",
}

/** The list fields a chip row edits. Derived from the domain type, so a rename breaks here. */
type ListField = {
  [K in keyof SearchConfig]-?: NonNullable<SearchConfig[K]> extends string[] ? K : never
}[keyof SearchConfig]

export class SearchProfileVm implements ViewModel {
  readonly view: Signal<SearchProfileView | null> = signal<SearchProfileView | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly saving: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** The transient derive proposal under HITL review (null = not reviewing). */
  readonly review: Signal<DerivedProfile | null> = signal<DerivedProfile | null>(null)
  /** True while the metered derive seam is in flight (it takes ~10–30s). */
  readonly deriving: Signal<boolean> = signal(false)
  /** The review's RLHF Feedback composer disclosure state. */
  readonly refineOpen: Signal<boolean> = signal(false)
  /** The secondary »Feineinstellungen anpassen« manual editor disclosure (closed by default). */
  readonly advancedOpen: Signal<boolean> = signal(false)
  /** The tuning sub-block's disclosure state, nested inside the manual editor. */
  readonly tuningOpen: Signal<boolean> = signal(false)
  /**
   * The W3 rubric, RENDERED here and edited in W3 (S567 / P-A). The Suchprofil says what is
   * searched for; the rubric says what a find is then measured against — the person could not
   * see the second half of that sentence without leaving the surface. This is a read-only
   * projection of `workshop.ContextProduced` on the profiler stream: ONE writer (W3), one
   * truth, rendered where it is consumed. Null = not authored yet (or the read failed) — the
   * section then simply does not render, never a fabricated default.
   */
  readonly rubric: Signal<RubricView | null> = signal<RubricView | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** Per-user backup of the PAID derive proposal — see `search-profile-draft`. */
  private readonly draft: SearchProfileReviewDraftStore

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    session: ShellSession,
  ) {
    this.t = i18n.t
    this.draft = createSearchProfileReviewDraftStore(session.user?.sub ?? null)
    this.renderDeps = [
      this.view,
      this.loaded,
      this.saving,
      this.error,
      this.review,
      this.deriving,
      this.refineOpen,
      this.advancedOpen,
      this.tuningOpen,
      this.rubric,
    ]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    // The rubric is fetched DEFENSIVELY and in parallel: it is secondary material, so a missing
    // or failing profiler read must never blank or delay the Suchprofil itself.
    const rubric = apiGet<RubricView>("/api/workshops/decision-profile").catch(() => null)
    // A draft only ever exists for a proposal that was never accepted, so restore it whatever
    // the server read does — an offline / just-expired session must not cost a paid derive.
    const rescued = this.draft.load()
    if (rescued) this.review.set(rescued.proposal)
    try {
      this.view.set(await apiGet<SearchProfileView>("/api/workshops/search-profile"))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
    this.rubric.set(await rubric)
  }

  /** The config currently on screen (the seed before anything is authored). */
  get config(): SearchConfig {
    return this.view.value?.config ?? DEFAULT_SEARCH_CONFIG
  }

  /** True once a brief exists — the person has a derived/authored profile to search with. */
  get hasProfile(): boolean {
    return this.config.roles.length > 0 || this.config.queries.length > 0
  }

  /** Is W1 complete? The derive reads W1 and the SAVE requires it, so before W1 the surface
   *  guides the person into it rather than offering a derive whose accept the server rejects. */
  get w1Ready(): boolean {
    return this.view.value?.w1Ready ?? false
  }

  /**
   * DERIVE a search profile from the person's values / statements / CV + optional free-text
   * intent (metered AI seam). Enters the review state with the proposal; the person confirms
   * it into their config via {@link acceptReview}. Feedback (the refine composer) re-enters
   * here with the feedback as the intent, replacing the proposal wholesale.
   */
  async derive(intent?: string): Promise<void> {
    if (this.deriving.value) return
    this.deriving.set(true)
    this.error.set(null)
    try {
      const trimmed = intent?.trim() ?? ""
      const body = trimmed.length > 0 ? { intent: trimmed } : {}
      const res = await apiPost<DeriveResult>("/api/workshops/search-profile/derive", body)
      this.enterReview(res.proposal)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.deriving.set(false)
    }
  }

  /**
   * Accept the reviewed proposal into the config. `roles = proposal.roles.map(r => r.term)`
   * and `queries = roles` (roles doubles as queries — the search builds its terms from both,
   * and keeping them in step is what makes an accepted proposal immediately produce queries);
   * the seniority / locations / exclusions / summary come straight from the proposal. The
   * per-role rationale is the review's »warum«, not part of the durable brief, so it is not
   * persisted. Saved over the WHOLE current config (the whole-config invariant).
   */
  async acceptReview(): Promise<void> {
    const proposal = this.review.value
    if (!proposal || this.saving.value) return
    // The accept saves the WHOLE config (`patch` spreads `this.config`), and without a loaded
    // view that spread falls back to DEFAULT_SEARCH_CONFIG — which would wipe the person's
    // sourceAllowlist / minTier / limit. A rescued proposal can outlive a FAILED read, so this
    // is reachable: refuse the destructive write and keep the proposal on screen.
    if (this.view.value === null) {
      this.error.set(this.t("sp.review.notLoaded"))
      return
    }
    const roles = proposal.roles.map((r) => r.term).filter((t) => t.length > 0)
    await this.patch({
      roles,
      queries: roles,
      seniority: proposal.seniority,
      locations: proposal.locations,
      excludeKeywords: proposal.excludeKeywords,
      summary: proposal.summary,
    })
    if (this.error.value === null) {
      this.review.set(null)
      this.refineOpen.set(false)
      // The proposal is in the durable config now — the local rescue copy has done its job.
      this.draft.clear()
    }
  }

  /**
   * Enter HITL review on a proposal and back it up at once. The derive is metered and slow, so
   * the copy must exist before anything can end the page's life — not on some later mutation
   * (there are none: the review is accept-or-refine, never edited in place).
   */
  private enterReview(proposal: DerivedProfile): void {
    this.review.set(proposal)
    this.refineOpen.set(false)
    this.draft.save({ proposal, updatedAt: new Date().toISOString() })
  }

  openRefine(): void {
    this.refineOpen.set(true)
  }

  cancelRefine(): void {
    this.refineOpen.set(false)
  }

  /** Feedback → re-derive with the feedback carried as the intent (replaces the proposal). */
  async submitRefine(feedback: string): Promise<void> {
    if (this.deriving.value || feedback.trim().length === 0) return
    await this.derive(feedback.trim())
  }

  /**
   * Persist a partial change over the CURRENT config and re-read.
   *
   * The whole config goes up every time: the fold replaces `targeting` wholesale, so a
   * partial save would destroy the fields this screen is not editing at that moment. (The
   * server completes the config as well — both ends hold the invariant.)
   */
  async patch(change: Partial<SearchConfig>): Promise<void> {
    if (this.saving.value) return
    this.saving.set(true)
    this.error.set(null)
    try {
      const config: SearchConfig = { ...this.config, ...change }
      this.view.set(await apiPost<SearchProfileView>("/api/workshops/search-profile", { config }))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.saving.set(false)
    }
  }

  /** Add a value to a list field (no duplicates, blanks ignored). */
  async addTo(field: ListField, raw: string): Promise<void> {
    const value = raw.trim()
    if (value.length === 0) return
    const current = this.config[field] ?? []
    if (current.includes(value)) return
    await this.patch({ [field]: [...current, value] })
  }

  async removeFrom(field: ListField, value: string): Promise<void> {
    const current = this.config[field] ?? []
    await this.patch({ [field]: current.filter((v) => v !== value) })
  }

  /** Toggle a fixed choice (seniority / exclusions / Remote) on a list field. */
  async toggle(field: ListField, value: string): Promise<void> {
    const current = this.config[field] ?? []
    return current.includes(value) ? this.removeFrom(field, value) : this.addTo(field, value)
  }

  /** Accept the deterministic CV/profile proposal for a field the person has not filled. */
  async acceptProposal(field: "roles" | "seniority" | "locations"): Promise<void> {
    const proposed = this.view.value?.proposal[field] ?? []
    if (proposed.length === 0) return
    const merged = [...new Set([...(this.config[field] ?? []), ...proposed])]
    // `roles` doubles as `queries` — the search builds its terms from both, and keeping them
    // in step is what makes an accepted proposal immediately produce queries.
    await this.patch(field === "roles" ? { roles: merged, queries: merged } : { [field]: merged })
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerSearchProfile extends KitElement<SearchProfileVm> {
  protected createViewModel(ctx: KitContext): SearchProfileVm {
    return new SearchProfileVm(ctx.inject(RouterToken), ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  // ── The derive-first primary area ────────────────────────────────────────────

  /** The pending state while the metered derive seam runs (~10–30s). */
  private pending(vm: SearchProfileVm): TemplateResult {
    return html`<section class="sp-section sp-derive" aria-labelledby="sp-derive-title" aria-busy="true">
      <h2 id="sp-derive-title" class="surface-section-title" lang="de">${vm.t("sp.derive.title")}</h2>
      <p class="sp-derive-pending" lang="de"><span class="sp-derive-spinner" aria-hidden="true"></span>${vm.t("sp.derive.pending")}</p>
    </section>`
  }

  /** The first-run invitation: an optional intent seam + the primary derive CTA. */
  private invitation(vm: SearchProfileVm): TemplateResult {
    return html`<section class="sp-section sp-derive" aria-labelledby="sp-derive-title">
      <h2 id="sp-derive-title" class="surface-section-title" lang="de">${vm.t("sp.derive.title")}</h2>
      <p class="sp-hint" lang="de">${vm.t("sp.derive.lead")}</p>
      <textarea
        class="rlhf-seam sp-intent"
        rows="1"
        ${seamAutoGrow()}
        placeholder=${vm.t("sp.derive.intent.placeholder")}
        aria-label=${vm.t("sp.derive.intent.placeholder")}
        lang="de"
      ></textarea>
      <div class="sp-derive-actions">
        <button class="btn-primary" data-act="sp-derive" lang="de">${vm.t("sp.derive.cta")}</button>
      </div>
      <p class="sp-derive-note" lang="de">${vm.t("sp.derive.note")}</p>
    </section>`
  }

  /** The returned proposal under review: the warm summary as the lead, then the roles with
   *  their per-person »warum«, the quiet meta lines, and the accept / re-derive / Feedback
   *  actions. The rationale is the whole point — it gets the legible line. */
  private reviewBlock(vm: SearchProfileVm, proposal: DerivedProfile): TemplateResult {
    return html`<section class="sp-section sp-review" aria-labelledby="sp-review-title">
      <p class="sp-review-badge" lang="de"><span class="chip is-draft">${vm.t("sp.review.badge")}</span>${vm.t("sp.review.note")}</p>
      <h2 id="sp-review-title" class="sp-review-summary" lang="de">${proposal.summary}</h2>

      <div class="sp-roles">
        <p class="sp-roles-label" lang="de">${vm.t("sp.review.rolesTitle")}</p>
        ${proposal.roles.map(
          (role) => html`<div class="sp-role">
            <p class="sp-role-term" lang="de">${role.term}</p>
            <p class="sp-role-why" lang="de">${role.rationale}</p>
          </div>`,
        )}
      </div>

      ${this.metaLines(vm, proposal)}

      <div class="sp-review-actions">
        <button class="btn-primary" data-act="sp-accept-review" ?disabled=${vm.saving.value} lang="de">${vm.t("sp.review.accept")}</button>
        <button class="sp-ghost" data-act="sp-rederive" ?disabled=${vm.deriving.value} lang="de">${vm.t("sp.review.rederive")}</button>
      </div>

      ${refineComposer(
        { open: vm.refineOpen.value, proposal: null, busy: vm.deriving.value },
        REFINE_ACTS,
        this.refineLabels(vm),
      )}
    </section>`
  }

  /** The saved profile: the durable summary as the lead, the role terms, then a »neu ableiten«
   *  affordance and the same Feedback composer, so the person can re-derive at any time. */
  private savedBlock(vm: SearchProfileVm): TemplateResult {
    const cfg = vm.config
    const summary = cfg.summary?.trim() ?? ""
    const roles = cfg.roles.length > 0 ? cfg.roles : cfg.queries
    return html`<section class="sp-section sp-review sp-saved" aria-labelledby="sp-saved-title">
      <p class="sp-roles-label" lang="de">${vm.t("sp.saved.title")}</p>
      ${summary.length > 0 ? html`<h2 id="sp-saved-title" class="sp-review-summary" lang="de">${summary}</h2>` : nothing}
      <div class="sp-chips sp-chips-static">
        ${roles.map((r) => html`<span class="sp-chip sp-chip-static" lang="de">${r}</span>`)}
      </div>
      ${this.metaLines(vm, {
        seniority: cfg.seniority ?? [],
        locations: cfg.locations ?? [],
        excludeKeywords: cfg.excludeKeywords ?? [],
      })}
      <div class="sp-review-actions">
        <button class="sp-ghost" data-act="sp-rederive" ?disabled=${vm.deriving.value} lang="de">${vm.t("sp.derive.again")}</button>
      </div>
      ${refineComposer(
        { open: vm.refineOpen.value, proposal: null, busy: vm.deriving.value },
        REFINE_ACTS,
        this.refineLabels(vm),
      )}
    </section>`
  }

  /** The quiet seniority / locations / exclusions meta lines (rendered only when non-empty). */
  private metaLines(vm: SearchProfileVm, p: Pick<DerivedProfile, "seniority" | "locations" | "excludeKeywords">): TemplateResult | typeof nothing {
    const line = (labelKey: CareerKey, values: readonly string[]): TemplateResult | typeof nothing =>
      values.length === 0
        ? nothing
        : html`<p class="sp-meta" lang="de"><span class="sp-meta-label">${vm.t(labelKey)}</span> ${values.join(" · ")}</p>`
    const seniority = line("sp.review.seniority", p.seniority)
    const locations = line("sp.review.locations", p.locations)
    const exclude = line("sp.review.exclude", p.excludeKeywords)
    if (seniority === nothing && locations === nothing && exclude === nothing) return nothing
    return html`<div class="sp-metas">${seniority}${locations}${exclude}</div>`
  }

  /** The (already-t()'d) labels for the shared refine composer. */
  private refineLabels(vm: SearchProfileVm): Parameters<typeof refineComposer>[2] {
    return {
      open: vm.t("sp.refine.open"),
      placeholder: vm.t("sp.refine.placeholder"),
      submit: vm.t("sp.refine.submit"),
      submitting: vm.t("sp.refine.submitting"),
      cancel: vm.t("sp.refine.cancel"),
      // Present for the shared contract; the search-profile refine re-derives wholesale, so the
      // proposal sub-state never renders and these are never shown.
      proposalLabel: vm.t("sp.refine.proposalLabel"),
      accept: vm.t("sp.refine.accept"),
      again: vm.t("sp.refine.again"),
      discard: vm.t("sp.refine.discard"),
    }
  }

  // ── The secondary manual editor (disclosed »Feineinstellungen anpassen«) ──────

  /**
   * A removable chip row plus its add-field — the one place real containment is earned.
   *
   * `covered` are values a fixed choice row above already renders (and already shows as
   * selected). Repeating them as chips would show the same value twice with two different
   * affordances for the same act, so the chip row carries only the free-typed additions.
   */
  private chipEditor(vm: SearchProfileVm, field: ListField, placeholder: string, covered: readonly string[] = []): TemplateResult {
    const values = (vm.config[field] ?? []).filter((v) => !covered.includes(v))
    return html`
      <div class="sp-chips">
        ${values.map(
          (value) => html`<span class="sp-chip">
            <span lang="de">${value}</span>
            <button
              class="sp-chip-x"
              data-act="sp-remove"
              data-field=${field}
              data-value=${value}
              aria-label=${`${vm.t("sp.remove")}: ${value}`}
              lang="de"
            >×</button>
          </span>`,
        )}
      </div>
      <form class="sp-addform">
        <input class="sp-add" type="text" name=${field} placeholder=${placeholder} lang="de" />
        <button class="sp-addbtn" type="submit" data-act="sp-add" data-field=${field} lang="de">${vm.t("sp.add")}</button>
      </form>
    `
  }

  /** A row of fixed one-tap choices. */
  private choiceRow(vm: SearchProfileVm, field: ListField, choices: readonly string[]): TemplateResult {
    const values = vm.config[field] ?? []
    return html`<div class="sp-choices">
      ${choices.map((choice) => {
        const on = values.includes(choice)
        return html`<button
          class=${classMap({ "sp-choice": true, "is-on": on })}
          data-act="sp-toggle"
          data-field=${field}
          data-value=${choice}
          aria-pressed=${on ? "true" : "false"}
          lang="de"
        >${choice}</button>`
      })}
    </div>`
  }

  /** The offer to accept what the CV already says — shown only while the field is empty. */
  private proposalOffer(vm: SearchProfileVm, field: "roles" | "seniority" | "locations"): TemplateResult | typeof nothing {
    const proposed = vm.view.value?.proposal[field] ?? []
    const current = vm.config[field] ?? []
    if (proposed.length === 0 || current.length > 0) return nothing
    return html`<p class="sp-proposal" lang="de">
      ${vm.t("sp.proposal.lead")}
      <span class="sp-proposal-values">${proposed.join(" · ")}</span>
      <button class="sp-linkbtn" data-act="sp-accept" data-field=${field} lang="de">${vm.t("sp.proposal.accept")}</button>
    </p>`
  }

  /** Result budget + quality floor + open-web reach — the fields no user could reach before. */
  private tuning(vm: SearchProfileVm): TemplateResult {
    const cfg = vm.config
    const open = vm.tuningOpen.value
    const floor = cfg.minTier ?? "none"
    return html`
      <section class="sp-section" aria-labelledby="sp-tuning-title">
        <h2 id="sp-tuning-title" class="surface-section-title" lang="de">
          <button class="sp-disclose" data-act="sp-toggle-tuning" aria-expanded=${open ? "true" : "false"} lang="de">
            ${vm.t("sp.tuning.title")}
          </button>
        </h2>
        ${open
          ? html`
              <p class="sp-hint" lang="de">${vm.t("sp.tuning.hint")}</p>
              <div class="sp-field">
                <span class="sp-field-label" lang="de">${vm.t("sp.tuning.minTier")}</span>
                <div class="sp-choices">
                  ${TIER_CHOICES.map((tier) => {
                    const on = floor === tier
                    return html`<button
                      class=${classMap({ "sp-choice": true, "is-on": on })}
                      data-act="sp-mintier"
                      data-tier=${tier}
                      aria-pressed=${on ? "true" : "false"}
                      lang="de"
                    >${vm.t(TIER_LABEL[tier])}</button>`
                  })}
                </div>
              </div>
              <div class="sp-field">
                <span class="sp-field-label" lang="de">${vm.t("sp.tuning.limit")}</span>
                <div class="sp-choices">
                  ${LIMIT_CHOICES.map((limit) => {
                    const on = (cfg.limit ?? DEFAULT_SEARCH_CONFIG.limit) === limit
                    return html`<button
                      class=${classMap({ "sp-choice": true, "is-on": on })}
                      data-act="sp-limit"
                      data-limit=${String(limit)}
                      aria-pressed=${on ? "true" : "false"}
                      lang="de"
                    >${limit}</button>`
                  })}
                </div>
              </div>
              <div class="sp-field">
                <span class="sp-field-label" lang="de">${vm.t("sp.tuning.openWeb")}</span>
                <div class="sp-choices">
                  <button
                    class=${classMap({ "sp-choice": true, "is-on": cfg.allowCompanyWebsites ?? true })}
                    data-act="sp-openweb"
                    aria-pressed=${(cfg.allowCompanyWebsites ?? true) ? "true" : "false"}
                    lang="de"
                  >${(cfg.allowCompanyWebsites ?? true) ? vm.t("sp.tuning.openWeb.on") : vm.t("sp.tuning.openWeb.off")}</button>
                </div>
              </div>
            `
          : nothing}
      </section>
    `
  }

  /**
   * The whole manual editor, disclosed as a secondary »Feineinstellungen anpassen« section
   * (§0 adaptive disclosure, closed by default). The person derived their profile above; this
   * is the fallback for anyone who wants to touch a field directly.
   */
  private advanced(vm: SearchProfileVm): TemplateResult {
    const open = vm.advancedOpen.value
    return html`
      <section class="sp-section sp-advanced" aria-labelledby="sp-advanced-title">
        <h2 id="sp-advanced-title" class="surface-section-title" lang="de">
          <button class="sp-disclose" data-act="sp-toggle-advanced" aria-expanded=${open ? "true" : "false"} lang="de">
            ${vm.t("sp.advanced.title")}
          </button>
        </h2>
        ${open
          ? html`
              <p class="sp-hint" lang="de">${vm.t("sp.advanced.hint")}</p>

              <div class="sp-adv-field" aria-labelledby="sp-roles-title">
                <h3 id="sp-roles-title" class="sp-adv-label" lang="de">${vm.t("sp.roles.title")}</h3>
                <p class="sp-hint" lang="de">${vm.t("sp.roles.hint")}</p>
                ${this.proposalOffer(vm, "roles")} ${this.chipEditor(vm, "roles", vm.t("sp.roles.placeholder"))}
              </div>

              <div class="sp-adv-field" aria-labelledby="sp-level-title">
                <h3 id="sp-level-title" class="sp-adv-label" lang="de">${vm.t("sp.level.title")}</h3>
                <p class="sp-hint" lang="de">${vm.t("sp.level.hint")}</p>
                ${this.proposalOffer(vm, "seniority")} ${this.choiceRow(vm, "seniority", SENIORITY_CHOICES)}
              </div>

              <div class="sp-adv-field" aria-labelledby="sp-where-title">
                <h3 id="sp-where-title" class="sp-adv-label" lang="de">${vm.t("sp.where.title")}</h3>
                <p class="sp-hint" lang="de">${vm.t("sp.where.hint")}</p>
                ${this.proposalOffer(vm, "locations")} ${this.choiceRow(vm, "locations", [REMOTE])}
                ${this.chipEditor(vm, "locations", vm.t("sp.where.placeholder"), [REMOTE])}
              </div>

              <div class="sp-adv-field" aria-labelledby="sp-out-title">
                <h3 id="sp-out-title" class="sp-adv-label" lang="de">${vm.t("sp.exclude.title")}</h3>
                <p class="sp-hint" lang="de">${vm.t("sp.exclude.hint")}</p>
                ${this.choiceRow(vm, "excludeKeywords", EXCLUSION_CHOICES)}
                ${this.chipEditor(vm, "excludeKeywords", vm.t("sp.exclude.placeholder"), EXCLUSION_CHOICES)}
              </div>

              <div class="sp-adv-field" aria-labelledby="sp-matters-title">
                <h3 id="sp-matters-title" class="sp-adv-label" lang="de">${vm.t("sp.matters.title")}</h3>
                <p class="sp-hint" lang="de">${vm.t("sp.matters.hint")}</p>
                <form class="sp-textform">
                  <textarea class="rlhf-seam sp-matters-text" rows="1" ${seamAutoGrow()} name="summary" lang="de" .value=${vm.view.value?.authoredSummary ?? ""}></textarea>
                  <button class="sp-addbtn" type="submit" data-act="sp-summary" lang="de">${vm.t("sp.matters.save")}</button>
                </form>
              </div>

              ${this.tuning(vm)}
            `
          : nothing}
      </section>
    `
  }

  /**
   * The live preview. These are the ACTUAL query strings `search.start` would fire, built
   * server-side by the same `buildQueries` the pipeline uses. Reframed as TOOL-DERIVED and
   * secondary (»Daraus baut die Suche diese Anfragen«) so it reads as the machine's output,
   * not as the person's literal input — but kept truthful (it is the real search).
   */
  private preview(vm: SearchProfileVm): TemplateResult | typeof nothing {
    const view = vm.view.value
    const queries = view?.queries ?? []
    if (queries.length === 0) return nothing
    const shown = queries.slice(0, 4)
    return html`
      <section class="sp-section sp-preview" aria-labelledby="sp-preview-title">
        <h2 id="sp-preview-title" class="surface-section-title" lang="de">${vm.t("sp.preview.title")}</h2>
        <p class="sp-hint" lang="de">${vm.t("sp.preview.frame")}</p>
        <ol class="sp-query-list" role="list">
          ${shown.map((q) => html`<li class="sp-query" lang="de">${q}</li>`)}
        </ol>
        ${queries.length > shown.length
          ? html`<p class="sp-hint" lang="de">${vm.t("sp.preview.more").replace("{n}", String(queries.length - shown.length))}</p>`
          : nothing}
      </section>
    `
  }

  /**
   * »Wonach entschieden wird« — the W3 rubric, rendered read-only (S567 / P-A). The Suchprofil
   * answers WHAT is searched for; without this the person had to leave the surface to see what
   * a find is then measured AGAINST. Elicitation stays in W3 (the one writer), so this block
   * renders and links, never edits: two editors over one rubric would be exactly the duplicate
   * truth R6 rules out. Absent/unauthored rubric ⇒ nothing renders (no fabricated default).
   */
  private criteria(vm: SearchProfileVm): TemplateResult | typeof nothing {
    const rubric = vm.rubric.value
    if (!rubric?.authored || rubric.weights.length === 0) return nothing
    const pct = (w: number): string => `${Math.round(w * 100)} %`
    return html`
      <section class="sp-section sp-criteria" aria-labelledby="sp-criteria-title">
        <h2 id="sp-criteria-title" class="surface-section-title" lang="de">${vm.t("sp.criteria.title")}</h2>
        <p class="sp-hint" lang="de">${vm.t("sp.criteria.frame")}</p>
        <dl class="sp-criteria-list">
          ${rubric.weights.map(
            (w) => html`<div class="sp-criterion">
              <dt lang="de">${w.label}</dt>
              <dd lang="de">${pct(w.weight)}</dd>
            </div>`,
          )}
        </dl>
        ${rubric.antiValues.length > 0
          ? html`<p class="sp-criteria-anti" lang="de">
              <span class="sp-criteria-anti-label" lang="de">${vm.t("sp.criteria.antiValues")}</span>
              ${rubric.antiValues.join(" · ")}
            </p>`
          : nothing}
        <a class="sp-criteria-edit" href=${DECISION_PROFILE_ROUTE} data-act="nav" data-url=${DECISION_PROFILE_ROUTE} lang="de"
          >${vm.t("sp.criteria.edit")}</a
        >
      </section>
    `
  }

  /** The derive-first primary area: pending → review → saved → W1-gate → first-run invitation. */
  private primary(vm: SearchProfileVm): TemplateResult {
    const review = vm.review.value
    if (vm.deriving.value && !review) return this.pending(vm)
    if (review) return this.reviewBlock(vm, review)
    if (vm.hasProfile) return this.savedBlock(vm)
    // The derive reads W1 and the SAVE requires it — before W1 the derive would be untailored
    // and its accept would 500 on the `startWorkshop` prereq, so guide the person into W1 first.
    if (!vm.w1Ready) return this.w1Gate(vm)
    return this.invitation(vm)
  }

  /** Shown before W1 is complete: the derive has nothing to read yet, so send the person to
   *  Workshop 1 rather than offer a derive whose accept the server-side prereq rejects. */
  private w1Gate(vm: SearchProfileVm): TemplateResult {
    return html`<section class="sp-section sp-derive" aria-labelledby="sp-gate-title">
      <h2 id="sp-gate-title" class="surface-section-title" lang="de">${vm.t("sp.gate.title")}</h2>
      <p class="sp-hint" lang="de">${vm.t("sp.gate.lead")}</p>
      <div class="sp-derive-actions">
        <button class="btn-primary" data-act="nav" data-url="/workshops/values" lang="de">${vm.t("sp.gate.cta")}</button>
      </div>
    </section>`
  }

  protected render(vm: SearchProfileVm): TemplateResult {
    if (!vm.loaded.value) return html`<p class="surface-lead" lang="de">${vm.t("sp.loading")}</p>`

    const view = vm.view.value
    const ready = view?.canStart === true
    const decl = spineDecl("career-search-profile")

    return html`
      <div class="sp-doc">
        ${hubHero({
          eyebrow: vm.t("sp.eyebrow"),
          title: vm.t("sp.title"),
          lead: vm.t("sp.lead"),
          meta: ready ? vm.t("sp.state.ready") : vm.t("sp.state.blocked"),
        })}
        ${vm.error.value ? html`<p class="sp-error" role="alert" lang="de">${vm.error.value}</p>` : nothing}

        ${this.primary(vm)}
        ${this.preview(vm)}
        ${this.criteria(vm)}
        ${this.advanced(vm)}
        ${nextStep(
          vm.t("next.eyebrow"),
          decl ? toNextStep(decl, vm.t(decl.labelKey), ready ? undefined : vm.t("next.locked.searchProfile")) : undefined,
        )}
      </div>
    `
  }

  protected override actions(vm: SearchProfileVm): ActionMap {
    /** The `data-field` of the element an action fired from, narrowed to a list field. */
    const fieldOf = (event: Event): ListField | null => {
      const el = (event.target as HTMLElement).closest<HTMLElement>("[data-field]")
      const field = el?.dataset.field
      return field === "roles" || field === "queries" || field === "seniority" || field === "locations" || field === "excludeKeywords"
        ? field
        : null
    }
    return {
      // ── Derive-first primary ──
      "sp-derive": (_payload, event) => {
        const intent = (event.target as HTMLElement).closest(".sp-derive")?.querySelector<HTMLTextAreaElement>("textarea.sp-intent")?.value
        void vm.derive(intent)
      },
      "sp-rederive": () => void vm.derive(),
      "sp-accept-review": () => void vm.acceptReview(),
      "sp-refine-open": () => vm.openRefine(),
      "sp-refine-cancel": () => vm.cancelRefine(),
      "sp-refine-again": () => vm.cancelRefine(),
      "sp-refine-accept": () => {}, // the search-profile refine re-derives; no text-proposal accept
      "sp-refine-submit": (_payload, event) => {
        const root = (event.target as HTMLElement).closest(".rc-composer")
        void vm.submitRefine(readRefineFeedback(root))
      },
      // ── Secondary manual editor ──
      "sp-toggle-advanced": () => vm.advancedOpen.set(!vm.advancedOpen.value),
      "sp-remove": (_payload, event) => {
        const field = fieldOf(event)
        const value = (event.target as HTMLElement).closest<HTMLElement>("[data-value]")?.dataset.value
        if (field && value !== undefined) void vm.removeFrom(field, value)
      },
      "sp-toggle": (_payload, event) => {
        const field = fieldOf(event)
        const value = (event.target as HTMLElement).closest<HTMLElement>("[data-value]")?.dataset.value
        if (field && value !== undefined) void vm.toggle(field, value)
      },
      // The submit button carries the verb, so pressing Enter inside the text field activates
      // it natively — `preventDefault` stops the form actually navigating. Committing on an
      // explicit submit (never per keystroke) is what keeps a half-typed title from being saved.
      "sp-add": (_payload, event) => {
        event.preventDefault()
        const field = fieldOf(event)
        const form = (event.target as HTMLElement).closest("form")
        const input = form?.querySelector("input")
        if (!field || !input) return
        const raw = input.value
        input.value = ""
        void vm.addTo(field, raw)
      },
      "sp-accept": (_payload, event) => {
        const field = (event.target as HTMLElement).closest<HTMLElement>("[data-field]")?.dataset.field
        if (field === "roles" || field === "seniority" || field === "locations") void vm.acceptProposal(field)
      },
      "sp-summary": (_payload, event) => {
        event.preventDefault()
        const textarea = (event.target as HTMLElement).closest("form")?.querySelector("textarea")
        if (textarea && textarea.value !== (vm.view.value?.authoredSummary ?? "")) void vm.patch({ summary: textarea.value })
      },
      "sp-toggle-tuning": () => vm.tuningOpen.set(!vm.tuningOpen.value),
      "sp-mintier": ({ tier: chosen }) => {
        // Narrow through the choice list rather than casting: an unrecognised value becomes
        // "no floor", the honest reading of an affordance we do not recognise.
        const tier = TIER_CHOICES.find((t) => t === chosen)
        void vm.patch({ minTier: tier === undefined || tier === "none" ? null : tier })
      },
      "sp-limit": ({ limit }) => {
        const parsed = Number.parseInt(limit ?? "", 10)
        if (Number.isFinite(parsed) && parsed > 0) void vm.patch({ limit: parsed })
      },
      "sp-openweb": () => void vm.patch({ allowCompanyWebsites: !(vm.config.allowCompanyWebsites ?? true) }),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
